import makeStyles from '@mui/styles/makeStyles';
import React, { ComponentProps, useMemo } from 'react';
import ActionsDialog from 'spw-components/dialogs/actions-dialog';
import { IProjectSpaceType } from 'spw-redux/modules/space_types/types';
import { IChildWorksheetSpaceType } from 'spw-redux/modules/calculators/types';
import { useSelector } from 'react-redux';
import { selectSelectedSpaceTypeCategory } from 'spw-redux/modules/worksheet/selectors';
import { selectCurrentScenario } from 'spw-redux/modules/calculators/selectors';
import { IProjectSpaceTypeCategory } from 'spw-redux/modules/projects/types';
import { selectClientFacingNamesAreVisible } from 'spw-redux/modules/space_types/selectors';

const useStyles = makeStyles({
  container: {
    width: 'auto',
    maxWidth: '420px',
    '& button': {
      width: '88px',
    },
  },
});

type Props = {
  projectSpaceType?: IProjectSpaceType;
  childSpaceType?: IChildWorksheetSpaceType;
  onConfirm: () => void;
  onClose: () => void;
  open: boolean;
};

const HideSpaceTypeConfirmationDialog = ({
  projectSpaceType,
  childSpaceType,
  onConfirm,
  onClose,
  open,
}: Props) => {
  const classes = useStyles();
  const selectedSpaceTypeCategory = useSelector(
    selectSelectedSpaceTypeCategory
  );
  const clientFacingNamesAreVisible = useSelector(
    selectClientFacingNamesAreVisible
  );
  const currentScenario = useSelector(selectCurrentScenario);

  const spaceTypeName = useMemo(
    () =>
      clientFacingNamesAreVisible
        ? projectSpaceType?.clientFacingName
        : projectSpaceType?.spaceType?.name,
    [clientFacingNamesAreVisible, projectSpaceType]
  );

  const childSpaceTypeName = useMemo(
    () =>
      clientFacingNamesAreVisible
        ? childSpaceType?.clientFacingName
        : childSpaceType?.name,
    [clientFacingNamesAreVisible, childSpaceType]
  );

  const actions: ComponentProps<typeof ActionsDialog>['actions'] = [
    {
      text: 'HIDE',
      onClick: onConfirm,
      variant: 'delete',
    },
    {
      text: 'CANCEL',
      onClick: onClose,
      variant: 'cancel',
    },
  ];

  let title = 'Hide Space Type';
  let description =
    'Values entered for this space type will be deleted on this worksheet only. You can add back space types with empty values at any time.';

  if ((selectedSpaceTypeCategory as IProjectSpaceTypeCategory)?.subcategories) {
    title = `Hide ${selectedSpaceTypeCategory.name}`;
    description =
      'Values entered for all space types within this space category will be deleted on this worksheet only. You can add back space types with empty values at any time.';
  } else if (selectedSpaceTypeCategory) {
    title = `Hide ${selectedSpaceTypeCategory.name}`;
    description =
      'Values entered for all space types within this subcategory will be deleted on this worksheet only. You can add back space types with empty values at any time.';
  } else if (childSpaceType) {
    title = `Hide ${childSpaceTypeName}`;
    description =
      'Values entered for this space type will be deleted on this worksheet only. You can add back space types with empty values at any time.';
  } else if (projectSpaceType) {
    title = `Hide ${spaceTypeName}`;
    if (currentScenario.isDetailed)
      description =
        'Values entered for these space types will be deleted on this worksheet only. You can add back space types with empty values at any time.';
  }

  if (!open) return null;

  return (
    <ActionsDialog
      isOpen={open}
      title={title}
      description={description}
      actions={actions}
      containerClassName={classes.container}
    />
  );
};

export default HideSpaceTypeConfirmationDialog;
